import React from 'react';
import { Link } from 'react-router-dom';
import { PlayIcon } from 'lucide-react';
import { slots } from '../../data/slots';
import type { Slot } from '../../types/slot';
import { useSiteSettings } from '../../contexts/SiteSettingsContext';
import { formatRtp, getCasinosForSlot } from '../../utils/slots';
import { SlotThumb } from '../SlotThumb';
import { ScoreBadge } from '../ScoreBadge';
import { RealMoneyButton } from '../RealMoneyButton';
export function HomeHero({
  onDemo
}: {
  onDemo: (s: Slot) => void;
}) {
  const {
    showRealMoneyCtas
  } = useSiteSettings();
  const top = slots[0];
  const runnersUp = slots.slice(1, 5);
  const casino = getCasinosForSlot(top)[0];
  return <section className="mx-auto grid max-w-7xl gap-10 px-4 pb-16 pt-10 sm:px-6 lg:grid-cols-[1.1fr_1fr] lg:gap-14 lg:pt-16" data-id="element-95" data-mp="2s3519hfvx1qu">
      <div className="flex flex-col justify-center" data-id="element-96" data-mp="3fry31uj6u3td">
        <p className="text-sm font-medium text-fern" data-id="element-97" data-mp="3kjpknfkd8z7d">De Nederlandse gids voor online slots in Nieuw-Zeeland</p>
        <h1 className="mt-4 font-serif text-4xl font-semibold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl" data-id="element-98" data-mp="ouz8y34h50vo">
          Top 30 beste online slots in Nieuw-Zeeland
        </h1>
        <p className="mt-5 max-w-xl text-lg leading-relaxed text-muted" data-id="element-99" data-mp="jub8kqucv7os">
          De online slots — of pokies, zoals Kiwi’s zeggen — die nu het meest gespeeld worden, onafhankelijk getest
          voor Nederlandstalige spelers. Speel elke slot eerst gratis, en kies daarna een casino dat de game met echt
          geld aanbiedt.
        </p>

        <div className="mt-8" data-id="element-100" data-mp="12lkjn4jry4ih">
          <p className="text-sm font-medium text-ink" data-id="element-101" data-mp="12mdvgjxlbk7h">Ook in de top 5</p>
          <ul className="mt-3 flex flex-wrap gap-2" data-id="element-102" data-mp="1gl3q7swxt7so">
            {runnersUp.map(s => <li key={s.slug} data-id="element-103" data-mp="1cluwup5t7bxn">
                <Link to={`/slots/${s.slug}`} className="inline-flex items-center gap-2 rounded-full border border-line bg-surface py-1 pl-1 pr-3.5 text-sm font-medium transition-colors duration-150 hover:border-ink/30" data-id="element-104" data-mp="1cll2yqhn8kmo">
                  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-paper font-serif text-sm text-muted" data-id="element-105" data-mp="1hcu0w800zb50">
                    {s.rank}
                  </span>
                  <span className="whitespace-nowrap" data-id="element-106" data-mp="1r9gimnx21m2t">{s.name}</span>
                </Link>
              </li>)}
          </ul>
        </div>

        <p className="mt-8 text-sm text-muted" data-id="element-107" data-mp="1w6gfq9bxfehs">
          Laatst bijgewerkt op <time dateTime="2026-10-06" data-id="element-108" data-mp="209v9e2ssftdp">6 oktober 2026</time> · <Link to="/#methode" className="font-medium text-ink underline underline-offset-2" data-id="element-109" data-mp="2597uai03mqdn">Zo bepalen we de score</Link>
        </p>
      </div>

      <article className="rounded-3xl bg-ink p-6 text-white sm:p-8" aria-labelledby="top-slot-title" data-id="element-110" data-mp="2a1iwxsm84q9w">
        <div className="flex items-start justify-between gap-4" data-id="element-111" data-mp="2eyreps3gmsam">
          <p className="rounded-full bg-oranje px-3 py-1 text-xs font-semibold" data-id="element-112" data-mp="2jz6pqd09rnpf">#1 in onze Top 30</p>
          <ScoreBadge score={top.score} size="lg" data-id="element-113" data-mp="2nz7g8w0n7pcu" />
        </div>
        <div className="mt-6 flex items-center gap-5" data-id="element-114" data-mp="2nxmnnbxw2ghm">
          <SlotThumb slot={top} size="lg" data-id="element-115" data-mp="cdez1de5lq8z" />
          <div data-id="element-116" data-mp="8b1v8uiyw2eu">
            <h2 id="top-slot-title" className="font-serif text-3xl font-semibold leading-tight" data-id="element-117" data-mp="8bac0xi7nrvb">
              {top.name}
            </h2>
            <p className="mt-1 text-white/70" data-id="element-118" data-mp="3cqoisnyy80u">{top.provider}</p>
          </div>
        </div>
        <p className="mt-6 leading-relaxed text-white/85" data-id="element-119" data-mp="3ujjx4hl3d9fe">{top.verdict}</p>
        <dl className="mt-6 grid grid-cols-3 divide-x divide-white/15 rounded-xl bg-white/5 py-3 text-center" data-id="element-120" data-mp="w14jezjrgo86">
          <div data-id="element-121" data-mp="r2kx2y199afa">
            <dt className="text-xs text-white/60" data-id="element-122" data-mp="r2urqyx8w5rb">RTP</dt>
            <dd className="mt-0.5 font-semibold tabular-nums" data-id="element-123" data-mp="majq9rmuwbwn">{formatRtp(top.rtp)}</dd>
          </div>
          <div data-id="element-124" data-mp="hc8f9dktrvcu">
            <dt className="text-xs text-white/60" data-id="element-125" data-mp="hci3owq81ugf">Volatiliteit</dt>
            <dd className="mt-0.5 font-semibold" data-id="element-126" data-mp="1er465fs4pr8f">{top.volatility}</dd>
          </div>
          <div data-id="element-127" data-mp="1esxer0maylj2">
            <dt className="text-xs text-white/60" data-id="element-128" data-mp="1a0nsbukec3lj">Max. winst</dt>
            <dd className="mt-0.5 font-semibold tabular-nums" data-id="element-129" data-mp="19z2y661zumo7">{top.maxWin}</dd>
          </div>
        </dl>
        <div className="mt-6 flex flex-col gap-2 sm:flex-row" data-id="element-130" data-mp="ukh29dplrxt7">
          <button type="button" onClick={() => onDemo(top)} className="inline-flex h-12 flex-1 items-center justify-center gap-2 rounded-lg bg-white px-5 font-semibold text-ink transition-transform duration-150 ease-out active:scale-[0.97]" data-id="element-131" data-mp="yl124p1k9fj1">
            <PlayIcon className="h-4 w-4 fill-current" aria-hidden="true" data-id="element-132" data-mp="1d8m3oeisf6ou" />
            Gratis demo
          </button>
          {showRealMoneyCtas ? <RealMoneyButton slot={top} size="lg" label="Speel met echt geld" className="flex-1" data-id="element-133" data-mp="1i9rweccg6tyd" /> : <Link to={`/slots/${top.slug}`} className="inline-flex h-12 flex-1 items-center justify-center rounded-lg border border-white/30 px-5 font-semibold hover:bg-white/10" data-id="element-134" data-mp="18i5kedsacquk">
              Lees de review
            </Link>}
        </div>
        {showRealMoneyCtas && casino && <p className="mt-3 text-center text-xs text-white/60" data-id="element-135" data-mp="3lntwzui9subm">
            Bij {casino.name}: {casino.bonus} · Advertentie · 18+ · Voorwaarden van toepassing
          </p>}
      </article>
    </section>;
}